"use client";
import {
    IAccountAssets,
    IBlocks,
    IBlog,
    IBlogPost,
    ITheme,
    IUserData,
} from "@/types";
import React from "react";
import { BlockUpdateFunction } from ".";
import AdminPopover from "../../content/AdminPopover";
import BlockToggle from "./tabs/toggle";
import Options from "./tabs/blogPostLatest/options";
import PostList from "../../blog/PostList";
import { BlogPostSkeletonBlock } from "../../blog/skeleton";
import { Separator } from "@/components/ui/separator";
import BlockMetadata from "./tabs/metadata";
interface Props {
    admin?: boolean;
    user: IUserData;
    block: IBlocks;
    update: BlockUpdateFunction;
    style: any;
    assets: IAccountAssets[];
    theme: ITheme;
    newBlock?: string | null;
    accountTheme: ITheme;
}
export default function BlockBlogPostLatest({
    admin,
    user,
    block,
    update,
    style,
    assets,
    theme,
    newBlock,
    accountTheme,
}: Props) {
    const [isExpanded, setIsExpanded] = React.useState(false);
    const [blockUpdate, setBlockUpdate] = React.useState(block);
    const [blog, setBlog] = React.useState<IBlog | null>(null);
    const [posts, setPosts] = React.useState<IBlogPost[] | null>(null);
    const [isLoading, setIsLoading] = React.useState(true);
    React.useEffect(() => {
        if (newBlock === block.id) {
            handleExpand();
        }
        return () => {
            window.removeEventListener("closeBlock", handleClose);
        };
    }, [newBlock, block.id]);
    React.useEffect(() => {
        window.addEventListener("closeBlock", {
            handleEvent: (event: CustomEvent) => {
                if (event.detail.id != block.id) {
                    if (isExpanded) {
                        setIsExpanded(false);
                    }
                }
            },
        });
    });
    React.useEffect(() => {
        if (block != blockUpdate) {
            setBlockUpdate(block);
        }
    }, [block]);
    const handleClose = () => {
        setIsExpanded(false);
        window.dispatchEvent(new CustomEvent("collapseBlock"));
    };
    function handleUpdate(value: IBlocks) {
        if (value == null) {
            update(blockUpdate.id, value, true);
            return;
        }
        setBlockUpdate({
            ...blockUpdate,
            ...value,
        });
        if (block == value) return;
        update(blockUpdate.id, value);
    }
    const event = new CustomEvent("expandBlock", {
        detail: {
            id: blockUpdate.id,
            title: "Latest Blog Post",
            description:
                "A block that displays the latest blog post from a blog.",
            titleOptions: (
                <BlockToggle
                    block={blockUpdate}
                    blockUpdate={setBlockUpdate}
                    update={handleUpdate}
                />
            ),
            content: (
                <div className="flex flex-col gap-3 pb-3">
                    <Options
                        key={block.id}
                        account={user}
                        block={blockUpdate}
                        blockUpdate={setBlockUpdate}
                        update={handleUpdate}
                    />
                    <Separator />
                    <BlockMetadata block={blockUpdate} update={handleUpdate} />
                </div>
            ),
        },
    });
    const handleExpand = () => {
        window.dispatchEvent(
            new CustomEvent("closeBlock", {
                detail: {
                    id: block.id,
                },
            })
        );
        setIsExpanded(true);
        window.dispatchEvent(event);
    };
    React.useEffect(() => {
        if (isExpanded) {
            window.dispatchEvent(event);
        }
    }, [blockUpdate]);
    React.useEffect(() => {
        async function getPost() {
            const res = await fetch(
                `/api/account/${user.id}/latest-post${blockUpdate.options?.limit ? `?limit=${blockUpdate.options?.limit}` : ""}`
            );
            const post = await res.json();
            if (post.data) {
                setPosts(post.data.posts);
                setBlog(post.data.blog);
                setIsLoading(false);
            }
        }
        getPost();
    }, [blockUpdate.options?.limit, user.id]);
    return (
        <AdminPopover
            admin={admin}
            className={block.show ? "" : "opacity-30"}
            onClick={handleExpand}
            title={"Blog Post Latest"}
            isOpen={isExpanded}
            onClose={handleClose}
            key={block.id}
        >
            <div
                key={block.id}
                className={`flex w-full flex-row flex-wrap gap-2 p-2 ${user.theme.button_rounded} relative`}
                id="BlogPostSingle"
            >
                <p
                    className="text-lg font-bold"
                    style={{
                        color: user.theme.text_col_heading,
                    }}
                >
                    {blockUpdate?.options?.name}
                </p>

                {isLoading && <BlogPostSkeletonBlock account={user} />}
                {!isLoading &&
                    blog &&
                    posts &&
                    posts.map((post, index) => (
                        <PostList
                            key={index}
                            blog={blog}
                            assets={[]}
                            post={{
                                ...post,
                                category_name:
                                    blog.categories.find(
                                        (e) => e.id == post.category
                                    )?.name || "",
                            }}
                            account={user}
                            accountTheme={accountTheme}
                        />
                    ))}
            </div>
        </AdminPopover>
    );
}
